import React, { useState, useEffect, useRef } from 'react';
import socketClient from '../utils/socketClient';

const ChatModal = ({ isOpen, onClose, offer, currentUser }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const [socketReady, setSocketReady] = useState(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Listen for socket auth success to allow room join
  useEffect(() => {
    const handleAuthed = () => {
      setSocketReady(true);
    };
    socketClient.on('socket_authenticated', handleAuthed);
    if (socketClient.isAuthenticated) setSocketReady(true);
    return () => socketClient.off('socket_authenticated', handleAuthed);
  }, []);

  useEffect(() => {
    if (isOpen && offer && socketReady) {
      const productId = offer.productId?._id || offer.productId;
      // Join offer room for real-time messages
      socketClient.joinOfferRoom(productId);

      // Listen for chat messages (optimistic update prevents duplicates via message._id check)
      const handleChatMessage = (data) => {
        if (data.productId === productId && data.message) {
          setMessages(prev => {
            // Remove ALL optimistic messages from the same sender when any real message arrives
            const filtered = prev.filter(m => 
              !(m.optimistic && (m.senderId?._id || m.senderId) === data.message.senderId._id)
            );
            
            // Check if this message already exists
            if (filtered.some(m => m._id === data.message._id)) {
              return prev; // Message already exists
            }
            
            return [...filtered, data.message];
          });
        }
      };

      socketClient.on('chat_message', handleChatMessage);

      // Load existing messages
      loadMessages(productId);

      // Mark messages as read when chat opens
      markMessagesAsRead(productId);

      return () => {
        socketClient.off('chat_message', handleChatMessage);
        socketClient.leaveOfferRoom(productId);
      };
    }
  }, [isOpen, offer, socketReady]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadMessages = async (productId) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const partnerId = currentUser._id === offer.buyerId._id ? offer.sellerId._id : offer.buyerId._id;
      
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/messages?partnerId=${partnerId}&productId=${productId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setMessages(Array.isArray(data.data) ? data.data : []);
      } else {
        console.error('Failed to load messages:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const markMessagesAsRead = async (productId) => {
    try {
      const token = localStorage.getItem('token');
      const partnerId = currentUser._id === offer.buyerId._id ? offer.sellerId._id : offer.buyerId._id;
      
      await fetch(`${process.env.REACT_APP_API_URL}/api/messages/mark-read`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          productId,
          partnerId
        })
      });

      // Also emit socket event for real-time read status
      socketClient.markMessagesAsRead(productId, partnerId);
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !offer) return;

    const receiverId = currentUser._id === offer.buyerId._id ? offer.sellerId._id : offer.buyerId._id;
    const productId = offer.productId?._id || offer.productId;
    const content = newMessage.trim();

    setNewMessage('');

    try {
      // Send message directly without optimistic update to avoid confusion
      socketClient.sendChatMessage(receiverId, productId, content);
    } catch (error) {
      console.error('Error sending message:', error);
      // Restore the message if sending failed
      setNewMessage(content);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!isOpen || !offer) return null;

  const otherUser = currentUser._id === offer.buyerId._id ? offer.sellerId : offer.buyerId;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg w-full max-w-2xl h-96 flex flex-col border border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <div>
            <h3 className="text-lg font-semibold text-white">
              Chat with {otherUser?.username || 'User'}
            </h3>
            <p className="text-sm text-gray-400">
              About: {offer.productId?.name || 'Product'}
            </p>
            {!socketReady && (
              <p className="text-xs text-yellow-400 mt-1">Connecting chat...</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            ✕
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {loading ? (
            <div className="text-center text-gray-400">Loading messages...</div>
          ) : messages.length === 0 ? (
            <div className="text-center text-gray-400">
              No messages yet. Start the conversation!
            </div>
          ) : (
            messages.map((message, index) => {
              const senderId = message.senderId?._id || message.senderId; // handle optimistic
              return (
                <div
                  key={message._id || index}
                  className={`flex ${
                    senderId === currentUser._id ? 'justify-end' : 'justify-start'
                  }`}
                >
                  <div
                    className={`max-w-xs px-4 py-3 rounded-lg shadow-sm ${
                      senderId === currentUser._id
                        ? 'bg-blue-600 text-white ml-auto'
                        : 'bg-gray-700 text-gray-100 mr-auto'
                    } ${message.optimistic ? 'opacity-70 border border-blue-400' : ''}`}
                  >
                    <p className="text-sm break-words leading-relaxed">{message.content}</p>
                    <p className={`text-xs mt-2 ${
                      senderId === currentUser._id ? 'text-blue-100' : 'text-gray-400'
                    }`}>
                      {new Date(message.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      {message.optimistic && <span className="ml-1 italic">(sending...)</span>}
                    </p>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Message Input */}
        <div className="p-4 border-t border-gray-700">
          <div className="flex space-x-2">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={socketReady ? 'Type your message...' : 'Connecting...' }
              disabled={!socketReady}
              className="flex-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            />
            <button
              onClick={sendMessage}
              disabled={!newMessage.trim() || !socketReady}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg transition-colors duration-300"
            >
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatModal;
